import { generate_secret_key } from './secret_generator'
import { rsa_256_encrypt } from './rsa_256'

const DB_NAME = 'SecureKeyDB'
const STORE_NAME = 'keys'
const KEY_ALIAS = 'secret_key'
const SESSION_ALIAS = 'session_id'
const LEGACY_STORAGE_KEY = 'secret_key'

let cachedMaterial = null
let pendingLoad = null

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'))
      return
    }
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = (event) => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
    request.onsuccess = (event) => resolve(event.target.result)
    request.onerror = () => reject(request.error)
  })
}

function idbGet(db, alias) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const req = store.get(alias)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbPut(db, alias, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const req = store.put(value, alias)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

function readLegacyLocalStorageKey() {
  if (typeof localStorage === 'undefined') return null
  try {
    return localStorage.getItem(LEGACY_STORAGE_KEY)
  } catch (e) {
    return null
  }
}

function clearLegacyLocalStorageKey() {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY)
  } catch (e) {
    // ignore
  }
}

async function importNonExtractableKey(secretString) {
  const keyData = new TextEncoder().encode(secretString)
  return window.crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'AES-CBC' },
    false,
    ['encrypt', 'decrypt']
  )
}

async function persistMaterial(db, cryptoKey, sessionId) {
  await idbPut(db, KEY_ALIAS, cryptoKey)
  await idbPut(db, SESSION_ALIAS, sessionId)
}

async function createMaterialFromPlainKey(db, plainKey) {
  const cryptoKey = await importNonExtractableKey(plainKey)
  const sessionId = rsa_256_encrypt(plainKey)
  await persistMaterial(db, cryptoKey, sessionId)
  clearLegacyLocalStorageKey()
  return { cryptoKey, sessionId }
}

async function loadOrCreateMaterial() {
  if (typeof window === 'undefined') {
    return { cryptoKey: null, sessionId: '' }
  }

  const db = await openDB()
  const storedKey = await idbGet(db, KEY_ALIAS)
  const storedSessionId = await idbGet(db, SESSION_ALIAS)
  const legacyKey = readLegacyLocalStorageKey()

  // Already have non-extractable CryptoKey + session_id
  if (storedKey instanceof CryptoKey && storedSessionId) {
    clearLegacyLocalStorageKey()
    return { cryptoKey: storedKey, sessionId: storedSessionId }
  }

  // IDB still has a plain string from earlier IndexedDB migration
  if (typeof storedKey === 'string' && storedKey) {
    return createMaterialFromPlainKey(db, storedKey)
  }

  // Migrate localStorage plain key, or generate a new one
  const plainKey = legacyKey || generate_secret_key()
  return createMaterialFromPlainKey(db, plainKey)
}

/**
 * Returns a non-extractable AES CryptoKey (IndexedDB) plus RSA session_id.
 * Raw key bytes are never persisted and cannot be exported.
 */
export async function getOrCreateEncryptionMaterial() {
  if (cachedMaterial) return cachedMaterial
  if (pendingLoad) return pendingLoad
  pendingLoad = loadOrCreateMaterial()
    .then((material) => {
      cachedMaterial = material
      return material
    })
    .finally(() => {
      pendingLoad = null
    })
  return pendingLoad
}
